/**
 * Section-based storage for retrofit surveys
 * 
 * This module provides targeted save/load functions for individual survey sections,
 * reducing IO pressure and enabling better scalability.
 * 
 * Benefits:
 * - Smaller writes (100-800 KB instead of 6 MB)
 * - No lock contention on main table
 * - Faster autosave
 * - Better for 1000+ concurrent users
 */

import { supabase } from '../lib/supabaseClient';

// ============================================================================
// TYPES
// ============================================================================

export interface SectionMetadata {
  id: string;
  survey_id: string;
  version: string;
  created_at: string;
  updated_at: string;
}

export interface MeasureSpecificationSection extends SectionMetadata {
  data: any; // Will be the measure_specification JSONB
}

// ============================================================================
// MEASURE SPECIFICATION SECTION
// ============================================================================

/**
 * Save measure specification section data
 * Uses UPSERT to handle both insert and update cases
 */
export async function saveMeasureSpecificationSection(
  surveyId: string,
  data: any
): Promise<{ success: boolean; error?: any }> {
  const startTime = performance.now();
  
  // Create a deep copy to avoid mutating the original object
  const dataToSave = JSON.parse(JSON.stringify(data));
  
  // Strip large dataUrl from all elevation types before saving
  // These images are regenerated for PDF export and don't need database storage
  const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
  let totalStripped = 0;
  
  elevationFields.forEach(field => {
    if (dataToSave && Array.isArray(dataToSave[field])) {
      dataToSave[field].forEach((elevation: any) => {
        let itemStripped = false;
        
        // Strip dataUrl (wall insulation elevations)
        if (elevation.dataUrl) {
          delete elevation.dataUrl;
          itemStripped = true;
        }
        
        // Strip base64String (RIR elevations)
        if (elevation.base64String) {
          delete elevation.base64String;
          itemStripped = true;
        }
        
        if (itemStripped) totalStripped++;
      });
    }
  });
  
  if (totalStripped > 0) {
    console.log(`🧹 [SAVE] Stripped ${totalStripped} elevation image(s) before saving`);
  }
  
  try {
    const originalSize = JSON.stringify(data).length;
    const strippedSize = JSON.stringify(dataToSave).length;
    
    console.log('[SectionStorage] 💾 Saving measure_specification section:', {
      surveyId,
      originalSize,
      strippedSize,
      reduction: `${((1 - strippedSize / originalSize) * 100).toFixed(1)}%`,
      timestamp: new Date().toISOString()
    });

    const { error } = await supabase
      .from('retrofit_survey_measure_specification')
      .upsert({
        survey_id: surveyId,
        data: dataToSave,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'survey_id' // Update if survey_id already exists
      });

    if (error) {
      console.error('[SectionStorage] ❌ Failed to save measure_specification:', error);
      return { success: false, error };
    }

    const duration = performance.now() - startTime;
    console.log('[SectionStorage] ✅ Saved measure_specification section:', {
      surveyId,
      duration: `${duration.toFixed(2)}ms`,
      timestamp: new Date().toISOString()
    });

    return { success: true };
  } catch (error) {
    console.error('[SectionStorage] ❌ Exception saving measure_specification:', error);
    return { success: false, error };
  }
}

/**
 * Load measure specification section data
 * Falls back to main table if section table is empty (for migration compatibility)
 */
export async function loadMeasureSpecificationSection(
  surveyId: string
): Promise<{ data: any | null; error?: any }> {
  try {
    console.log('[SectionStorage] 📥 Loading measure_specification section:', surveyId);

    // Try section table first
    const { data: sectionData, error: sectionError } = await supabase
      .from('retrofit_survey_measure_specification')
      .select('data')
      .eq('survey_id', surveyId)
      .single();

    if (sectionError && sectionError.code !== 'PGRST116') { // PGRST116 = not found
      console.error('[SectionStorage] ❌ Error loading from section table:', sectionError);
      return { data: null, error: sectionError };
    }

    if (sectionData?.data) {
      console.log('[SectionStorage] ✅ Loaded from section table');
      
      // Clean up large dataUrl from old database records (backwards compatibility)
      const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
      let strippedCount = 0;
      
      elevationFields.forEach(field => {
        if (Array.isArray(sectionData.data[field])) {
          sectionData.data[field].forEach((elevation: any) => {
            let itemStripped = false;
            
            if (elevation.dataUrl) {
              delete elevation.dataUrl;
              itemStripped = true;
            }
            
            if (elevation.base64String) {
              delete elevation.base64String;
              itemStripped = true;
            }
            
            if (itemStripped) strippedCount++;
          });
        }
      });
      
      if (strippedCount > 0) {
        console.log(`🧹 [SECTION LOAD] Stripped ${strippedCount} elevation dataUrl(s) from old database record`);
      }
      
      return { data: sectionData.data };
    }

    // Fallback: try main table (for backward compatibility during migration)
    console.log('[SectionStorage] 🔄 Section table empty, checking main table...');
    const { data: mainData, error: mainError } = await supabase
      .from('retrofit_surveys')
      .select('measure_specification')
      .eq('id', surveyId)
      .single();

    if (mainError) {
      console.error('[SectionStorage] ❌ Error loading from main table:', mainError);
      return { data: null, error: mainError };
    }

    if (mainData?.measure_specification) {
      console.log('[SectionStorage] ✅ Loaded from main table (fallback), migrating to section table...');
      
      // Clean up large dataUrl from old database records (backwards compatibility)
      const elevationFields = ['wallInsulationElevations', 'rirExteriorElevations'];
      let strippedCount = 0;
      
      elevationFields.forEach(field => {
        if (Array.isArray(mainData.measure_specification[field])) {
          mainData.measure_specification[field].forEach((elevation: any) => {
            let itemStripped = false;
            
            if (elevation.dataUrl) {
              delete elevation.dataUrl;
              itemStripped = true;
            }
            
            if (elevation.base64String) {
              delete elevation.base64String;
              itemStripped = true;
            }
            
            if (itemStripped) strippedCount++;
          });
        }
      });
      
      if (strippedCount > 0) {
        console.log(`🧹 [MAIN TABLE LOAD] Stripped ${strippedCount} elevation dataUrl(s) from old database record`);
      }
      
      // Auto-migrate to section table (will save cleaned data without dataUrl)
      await saveMeasureSpecificationSection(surveyId, mainData.measure_specification);
      
      return { data: mainData.measure_specification };
    }

    console.log('[SectionStorage] ℹ️ No measure_specification data found');
    return { data: null };
  } catch (error) {
    console.error('[SectionStorage] ❌ Exception loading measure_specification:', error);
    return { data: null, error };
  }
}

/**
 * Delete measure specification section
 */
export async function deleteMeasureSpecificationSection(
  surveyId: string
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('retrofit_survey_measure_specification')
      .delete()
      .eq('survey_id', surveyId);

    if (error) {
      console.error('[SectionStorage] ❌ Failed to delete measure_specification:', error);
      return { success: false, error };
    }

    console.log('[SectionStorage] ✅ Deleted measure_specification section:', surveyId);
    return { success: true };
  } catch (error) {
    console.error('[SectionStorage] ❌ Exception deleting measure_specification:', error);
    return { success: false, error };
  }
}

// ============================================================================
// HELPER: Get section metadata
// ============================================================================

export async function getMeasureSpecificationMetadata(
  surveyId: string
): Promise<{ metadata: SectionMetadata | null; error?: any }> {
  try {
    const { data, error } = await supabase
      .from('retrofit_survey_measure_specification')
      .select('id, survey_id, version, created_at, updated_at')
      .eq('survey_id', surveyId)
      .single();

    if (error && error.code !== 'PGRST116') {
      return { metadata: null, error };
    }

    return { metadata: data };
  } catch (error) {
    return { metadata: null, error };
  }
}

// ============================================================================
// FUTURE: Add functions for other sections as needed
// ============================================================================

// export async function saveRoomDetailsSection(surveyId: string, data: any) { ... }
// export async function loadRoomDetailsSection(surveyId: string) { ... }
// export async function saveFloorPlanSection(surveyId: string, data: any) { ... }
// export async function loadFloorPlanSection(surveyId: string) { ... }
